import './formbuilder.scss';

import { FetchManager, FriendlyCaptcha, ConditionalLogic, DropzoneHandler } from 'js-pimcore-formbuilder';
import { createElementFromHTML, smoothScrollTo, getOffset } from '../../utilities/js/helper';

class FormBuilder {
    constructor ($element, options) {
        const defaults = {
            initAttr: 'data-form',
            buttonDisabledClass: 'button--disabled',
            scrollOffset: 32,
        };

        this.settings = Object.assign({}, defaults, options);

        this.$formBuilder = $element;
        this.$form = this.$formBuilder.querySelector('form');
        this.$submitButton = this.$form.querySelector('button[type="submit"]');
        this.$successTarget = this.$formBuilder.querySelector('[data-formbuilder="success"]');
        this.scrollOffset = this.settings.scrollOffset;
        this.activateCaptcha = false;

        this.$header = document.querySelector('[data-siteheader="root"]');

        if (this.$header) {
            this.scrollOffset = this.scrollOffset + this.$header.offsetHeight;
        }

        if (!this.$successTarget) {
            this.$successTarget = this.$formBuilder.closest('[data-formbuilder="success"]');
        }
    }

    initialize () {
        this.$form.addEventListener('submit', () => {
            if (this.$submitButton) {
                this.BtnLoader = createElementFromHTML(window.OPTIONS.loader);
                this.BtnLoader.classList.add('loading--btn');
                this.$submitButton.appendChild(this.BtnLoader);
            }
        });

        if (this.$form) {
            // this.initializeSelect(this.$form);
            this.initializeUploader(this.$form);
            this.initCaptcha(this.$form, this.$submitButton);
        }

        const options = {
            hideElementClass: 'fb-cl-hide-element',
            elementTransformer: {
                show: (field, className) => {
                    field.classList.remove(className);
                    const $fieldGroup = field.closest('.form-field');
                    if ($fieldGroup) {
                        $fieldGroup.classList.remove(className);
                    }
                },
                hide: (field, className) => {
                    field.classList.add(className);
                    const $fieldGroup = field.closest('.form-field');
                    if ($fieldGroup) {
                        $fieldGroup.classList.add(className);
                    }
                },
                addRequiredState: (field) => {
                    field.setAttribute('required', 'required');
                    field.parentElement.classList.add('form-input--required');
                },
                removeRequiredState: (field) => {
                    field.removeAttribute('required');
                    field.parentElement.classList.remove('form-input--required');
                },
            }
        };

        new ConditionalLogic(this.$form, options); // eslint-disable-line

        this.evaluateInitialConditionalLogic();

        new FetchManager(this.$form, { // eslint-disable-line
            submitBtnSelector: 'button[type="submit"]',
            onSuccess: (messages, redirect, form) => {
                if (messages.length > 0) {
                    const successMessage = messages[0];

                    if (typeof successMessage.type !== 'undefined' && typeof successMessage.message !== 'undefined') {
                        if (successMessage.type === 'success') {
                            this.$successTarget.innerHTML = '';
                            this.$successTarget.appendChild(createElementFromHTML(successMessage.message));
                            this.$form.remove();

                            window.dispatchEvent(
                                new CustomEvent('content.loaded', {
                                    view: window,
                                    bubbles: true,
                                    cancelable: false,
                                    detail: {
                                        $context: this.$successTarget
                                    }
                                })
                            );

                            smoothScrollTo(0, getOffset(this.$successTarget).top - this.scrollOffset, 400);
                        }
                    }
                } else {
                    if (redirect !== '') {
                        window.location.href = redirect;
                    }
                }
            },
            onFail: (response, form) => {
                console.log(response, form);
                if (response.validation_errors) {
                    this.generateFormErrors(response.validation_errors);
                }
            },
            onGeneralError: (generalErrorMessages, form) => {
                console.log(generalErrorMessages);
            },
            onRequestDone: (form) => {
                this.$submitButton.querySelector('.loading')?.remove();
            },
            elementTransformer: {
                addValidationMessage: (form, field, messages) => {
                    const $field = field.closest('.form-field');
                    if (!$field) {
                        return;
                    }

                    $field.classList.add('form-field--error');
                    if (messages && messages.length > 0) {
                        this.generateFieldErrors($field, messages);
                    }
                },
                removeFormValidations: (form) => {
                    form.querySelectorAll('.form-field--error').forEach((el) => el.classList.remove('form-field--error'));
                    form.querySelectorAll('.form-errors').forEach((el) => el.remove());
                }
            }
        });
    }

    evaluateInitialConditionalLogic () {
        const processedRadioGroups = new Set();

        this.$form.querySelectorAll('input, select, textarea').forEach(($field) => {
            if ($field.type === 'radio') {
                if ($field.checked && !processedRadioGroups.has($field.name)) {
                    processedRadioGroups.add($field.name);
                    $field.dispatchEvent(new Event('change', { bubbles: true }));
                }
            } else if ($field.type === 'checkbox') {
                if ($field.checked) {
                    $field.dispatchEvent(new Event('change', { bubbles: true }));
                }
            } else if ($field.value) {
                $field.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    }

    initCaptcha ($form, $button) {
        const $friendlyCaptcha = $form.querySelector('.frc-captcha');

        if ($friendlyCaptcha) {
            $button.setAttribute('disabled', 'disabled');

            this.initCaptchaByFirstInput();

            $form.addEventListener('change', () => {
                this.checkValidationAndCaptcha($button);
            });
        }
    }

    checkValidationAndCaptcha ($button) {
        if (this.activateCaptcha) {
            $button.removeAttribute('disabled');
        } else {
            $button.setAttribute('disabled', 'disabled');
        }
    }

    initCaptchaByFirstInput () {
        const $allFormInputs = this.$form.querySelectorAll('.form-input');

        const handleFocusIn = (event) => {
            if (this.activateCaptcha === false) {
                this.activateCaptcha = true;
                new FriendlyCaptcha(this.$form); // eslint-disable-line

                $allFormInputs.forEach($formInput => {
                    $formInput.removeEventListener('focusin', handleFocusIn); // remove unused event listener
                });
            }
        };

        if ($allFormInputs.length > 0) {
            $allFormInputs.forEach($formInput => {
                $formInput.addEventListener('focusin', handleFocusIn); // add event listener for focus within the form input
            });
        }
    }

    initializeSelect ($form) {
        this.selects = [];
        this.$$selects = $form.querySelectorAll('select[data-select-form]');

        this.$$selects.forEach(($select, index) => {
            const choicesID = $select.id;
            const multipleChoices = $select.dataset.selectMultiple === 'true' ? true : false;
            const usePlaceholder = $select.dataset.selectPlaceholder === 'true' ? true : false;

            this.selects[index] = new Choices($select, {
                closeDropdownOnSelect: 'auto',
                removeItem: true,
                removeItemButton: multipleChoices,
                renderSelectedChoices: 'always',
                placeholder: usePlaceholder,
                placeholderValue: null,
                searchEnabled: false,
                searchChoices: false,
                itemSelectText: '',
                singleModeForMultiSelect: multipleChoices,
                shouldSort: false,
            });

            const $choicesInner = $select.closest('.choices__inner');
            const $choices = $choicesInner?.parentElement;

            $choices.setAttribute('aria-label', $select.dataset.selectLabel);

            const autoCloseSelect = () => {
                if ($choices.classList.contains('is-open')) {
                    $choices.parentElement.click();
                    $choices.blur();
                }
            };

            if (multipleChoices) {
                $choices.setAttribute('tabindex', '0');

                const $choicesListMultiple = $choices.querySelector('.choices__list--multiple');
                const $choicesList = $choices.querySelector('.choices__list--dropdown');
                const $choicesSearch = $choices.querySelector('input[type="search"]');

                if (choicesID != null) {
                    $choices.setAttribute('aria-controls', choicesID);
                    $choicesList.id = choicesID;
                    $choicesListMultiple.setAttribute('role', 'presentation');
                    $choicesSearch.remove(); // we remove the search input because we don't need it here

                    // deselect items from dropdown
                    $choicesList.addEventListener('click', (event) => {
                        const $choiceItem = event.target.closest('[data-id]');
                        if ($choiceItem) {
                            this.selects[index].removeActiveItemsByValue($choiceItem.dataset.value);
                        }
                    });

                    // close on tab out - because we can not tab through the options we use this event to close the dropdown
                    $choices.addEventListener('keydown', (e) => {
                        if (e.code === 'Tab' || e.code === 'Tabs') {
                            autoCloseSelect();
                        }
                    });

                    // close multi choice dropdown
                    $choices.onclick = () => {
                        autoCloseSelect();
                    };
                }
            }
        });
    }

    initializeUploader ($form) {
        const formbuilderFormInstance = this; // explanation - we have to save our constructor "this" to use our defined functions
        this.$dropzone = $form.querySelector('[data-js-handler="drop-zone"]');

        if (this.$dropzone) {
            const $field = this.$dropzone.closest('.form-field');

            if (this.$dropzone.hasAttribute('aria-required')) {
                this.$dropzone.removeAttribute('aria-required');
            }

            let maxFilesize = 10;
            let acceptedFiles = '.pdf,.jpg,.jpeg,.png,.heic';
            let hasError = false;
            let programaticallyRemoved = false;

            this.uploaderData = this.$dropzone ? JSON.parse(this.$dropzone.dataset.engineOptions) : {};
            this.uploaderTranslations = this.$dropzone ? JSON.parse(this.$dropzone.parentNode.dataset.formbuilderformUploaderMessages) : {};

            // Replace defaults if set
            if (this.uploaderData.max_file_size !== null && this.uploaderData.max_file_size <= maxFilesize) {
                maxFilesize = this.uploaderData.max_file_size;
            }

            if (this.uploaderData.allowed_extensions !== '') {
                acceptedFiles = this.uploaderData.allowed_extensions;
            }

            // Messages
            const uploadeMessage = this.uploaderTranslations.uploadInfo;
            const maxFilesizeMessage = this.uploaderTranslations.maxFilesize.replace('%filesize%', maxFilesize);
            const acceptedFilesMessage = this.uploaderTranslations.acceptedFiles.replace('%formats%', acceptedFiles.replace(/\./g, ' '));

            new DropzoneHandler(this.$form, { // eslint-disable-line
                dropzoneOptions: {
                    dictDefaultMessage: `${uploadeMessage}<small>${acceptedFilesMessage}</small><small>${maxFilesizeMessage}</small>`,
                    maxFilesize,
                    acceptedFiles,
                    init: function () {
                        this.on('error', (file, message) => {
                            hasError = true;
                            programaticallyRemoved = true;
                            formbuilderFormInstance.generateFieldErrors($field, message);
                            $field.classList.add('form-field--error');
                        });
                        this.on('success', () => {
                            if (hasError || $field.classList.contains('form-field--error')) {
                                hasError = false;
                                formbuilderFormInstance.removeOldErrorMessages($field);
                            }
                        });
                        this.on('removedfile', () => {
                            if (programaticallyRemoved) {
                                programaticallyRemoved = false;
                                formbuilderFormInstance.removeOldErrorMessages($field);
                            } else if (hasError) {
                                hasError = false;
                                formbuilderFormInstance.generateFieldErrors($field, messages);
                            }
                        });
                    }
                }
            });
        }
    }

    removeOldErrorMessages ($element) {
        const $oldErrorMessages = $element.querySelectorAll('.form-errors');

        if ($oldErrorMessages.length > 0) {
            $element.classList.remove('form-field--error');
            $oldErrorMessages.forEach($oldErrorMessage => {
                $oldErrorMessage.remove();
            });
        }
    }

    createErrorContainer (global = false) {
        const $errorMessages = document.createElement('div');
        $errorMessages.classList.add('form-errors');

        if (global === true) {
            $errorMessages.classList.add('form-errors--grouped');
            $errorMessages.setAttribute('aria-live', 'assertive');
            $errorMessages.setAttribute('tabindex', 0);
            $errorMessages.setAttribute('role', 'alert');
        }

        const $errorList = document.createElement('ul');
        $errorList.classList.add('form-errors__list');

        $errorMessages.appendChild($errorList);

        const errorObject = {};
        errorObject.$errorMessages = $errorMessages;
        errorObject.$errorList = $errorList;

        return errorObject;
    }

    generateFieldErrors ($field, messages) {
        const errorObject = this.createErrorContainer();
        const $errorMessages = errorObject.$errorMessages;
        const $errorList = errorObject.$errorList;

        // Ensure messages is an array
        messages = Array.isArray(messages) ? messages : [messages];

        messages.forEach(message => {
            const $errorItem = document.createElement('li');
            $errorItem.classList.add('form-errors__list-item');
            const errorMessage = message;
            $errorItem.innerHTML = errorMessage;

            $errorList.appendChild($errorItem);
        });

        $field.appendChild($errorMessages);
    }

    generateFormErrors (messages) {
        const errorObject = this.createErrorContainer(true);
        const $errorMessages = errorObject.$errorMessages;
        const $errorList = errorObject.$errorList;

        $errorMessages.appendChild($errorList);

        for (const fieldId in messages) {
            const $input = document.getElementById(fieldId);
            let fieldName = '';

            if ($input) {
                if ($input.hasAttribute('data-errorname')) {
                    console.log($input.getAttribute('data-errorname'));
                    fieldName = $input.getAttribute('data-errorname');
                } else {
                    const $field = $input.closest('.form-field');
                    const $label = $field.querySelector('.form-label');

                    if ($label) {
                        fieldName = $label.innerText;
                    }
                }

                const $errorItem = document.createElement('li');
                $errorItem.classList.add('form-errors__list-item');
                const $field = '<a href="#' + fieldId + '">' + fieldName + '</a>';

                const errorMessage = formTranslations.fieldError.replace('%fieldname%', $field);

                $errorItem.innerHTML = errorMessage;

                $errorList.appendChild($errorItem);
            }
        }

        this.$form.insertBefore($errorMessages, this.$form.firstElementChild);

        smoothScrollTo(0, getOffset(this.$form).top - this.scrollOffset, 400);
    }
}

export { FormBuilder };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $formbuilder = $context.querySelector('[data-formbuilder="root"]');

        if ($formbuilder) {
            const formbuilder = new FormBuilder($formbuilder);
            formbuilder.initialize();
        }
    }
});
